import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Pipette, Sun, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  hexToHsl,
  hslToHex,
  isValidHex,
  getContrastTextColor,
  modernTagPresets,
  type HslColor,
} from "./colorUtils";

interface ColorWheelPickerProps {
  color: string;
  onChange: (color: string) => void;
  previewName?: string;
}

export function ColorWheelPicker({
  color,
  onChange,
  previewName,
}: ColorWheelPickerProps) {
  const wheelRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hexInput, setHexInput] = useState(color);

  // Keep internal HSL state so saturation/hue are preserved when sliding lightness or dragging
  const [hsl, setHsl] = useState<HslColor>(() => hexToHsl(color));

  // Sync internal HSL when parent `color` prop changes from outside (and not actively dragging)
  useEffect(() => {
    if (!isDragging) {
      const parsed = hexToHsl(color);
      setHsl(parsed);
      setHexInput(color.toUpperCase());
    }
  }, [color, isDragging]);

  // Update color from wheel coordinate
  const handleWheelCoord = useCallback(
    (clientX: number, clientY: number) => {
      const rect = wheelRef.current?.getBoundingClientRect();
      if (!rect) return;

      const radius = rect.width / 2;
      const centerX = rect.left + radius;
      const centerY = rect.top + radius;

      const dx = clientX - centerX;
      const dy = clientY - centerY;

      const distance = Math.sqrt(dx * dx + dy * dy);
      // Clamp saturation to 100%
      const clampedDist = Math.min(distance, radius);
      const sat = Math.round((clampedDist / radius) * 100);

      // In CSS conic-gradient from 90deg:
      // 0 rad (dx>0, dy=0) is Red (3 o'clock, 0 deg)
      const rad = Math.atan2(dy, dx);
      let deg = Math.round((rad * 180) / Math.PI);
      if (deg < 0) deg += 360;

      // Keep current lightness, default to 50% if extreme
      const currentL = hsl.l > 90 || hsl.l < 15 ? 50 : hsl.l;

      const newHsl: HslColor = { h: deg, s: sat, l: currentL };
      setHsl(newHsl);

      const newHex = hslToHex(newHsl.h, newHsl.s, newHsl.l);
      setHexInput(newHex.toUpperCase());
      onChange(newHex);
    },
    [hsl.l, onChange],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    handleWheelCoord(e.clientX, e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    handleWheelCoord(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Ignored if pointer wasn't captured
    }
  };

  // Lightness Slider Change
  const handleLightnessChange = (newL: number) => {
    const updated: HslColor = { ...hsl, l: newL };
    setHsl(updated);
    const newHex = hslToHex(updated.h, updated.s, updated.l);
    setHexInput(newHex.toUpperCase());
    onChange(newHex);
  };

  // Hex Text Input Handlers
  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    setHexInput(val);
    const formatted = val.startsWith("#") ? val : `#${val}`;
    if (isValidHex(formatted)) {
      setHsl(hexToHsl(formatted));
      onChange(formatted);
    }
  };

  // Calculate indicator thumb position on the chromatic circle (0-100%)
  const thumbPosition = useMemo(() => {
    const angleRad = (hsl.h * Math.PI) / 180;
    const distancePercent = hsl.s / 100; // 0 to 1
    // Radius is 50% of the circle width
    const x = 50 + distancePercent * 50 * Math.cos(angleRad);
    const y = 50 + distancePercent * 50 * Math.sin(angleRad);
    return {
      left: `${Math.max(0, Math.min(100, x))}%`,
      top: `${Math.max(0, Math.min(100, y))}%`,
    };
  }, [hsl.h, hsl.s]);

  const textColor = getContrastTextColor(color);

  return (
    <div className="space-y-3.5">
      {/* Top row: Label + Live Tag Preview */}
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          Círculo Cromático
        </Label>
        <div
          className="px-2.5 py-1 rounded-md text-xs font-semibold shadow-sm transition-all duration-200 border border-black/10"
          style={{ backgroundColor: color, color: textColor }}
        >
          {previewName?.trim() ? previewName.trim() : "Exemplo de Etiqueta"}
        </div>
      </div>

      {/* Main Color Wheel & Controls Container */}
      <div className="flex flex-col sm:flex-row items-center gap-4 p-3 bg-muted/40 rounded-xl border border-border/60">
        {/* Chromatic Circle */}
        <div className="relative flex-shrink-0">
          <div
            ref={wheelRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            className="w-36 h-36 sm:w-40 sm:h-40 rounded-full relative cursor-crosshair select-none touch-none shadow-md ring-1 ring-border/80 hover:ring-2 hover:ring-primary/40 transition-shadow"
            style={{
              background: `
                radial-gradient(circle at center, #ffffff 0%, rgba(255, 255, 255, 0.05) 90%, rgba(0, 0, 0, 0.15) 100%),
                conic-gradient(from 90deg, 
                  #ff0000 0deg, 
                  #ff7700 30deg,
                  #ffff00 60deg, 
                  #77ff00 90deg,
                  #00ff00 120deg, 
                  #00ff77 150deg,
                  #00ffff 180deg, 
                  #0077ff 210deg,
                  #0000ff 240deg, 
                  #7700ff 270deg,
                  #ff00ff 300deg, 
                  #ff0077 330deg,
                  #ff0000 360deg
                )
              `,
            }}
          >
            {/* Thumb Indicator */}
            <div
              className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg pointer-events-none ring-1 ring-black/30 transition-transform active:scale-125"
              style={{
                left: thumbPosition.left,
                top: thumbPosition.top,
                backgroundColor: color,
              }}
            />
          </div>
        </div>

        {/* Right side: Lightness slider + Hex input + Eyedropper */}
        <div className="flex-1 w-full space-y-3">
          {/* Lightness Slider */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-xs text-muted-foreground font-medium">
              <span className="flex items-center gap-1">
                <Sun className="w-3.5 h-3.5" />
                Luminosidade
              </span>
              <span>{hsl.l}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="90"
              value={hsl.l}
              onChange={(e) => handleLightnessChange(Number(e.target.value))}
              className="w-full h-3 rounded-lg appearance-none cursor-pointer border border-border/80 shadow-inner"
              style={{
                background: `linear-gradient(to right, 
                  ${hslToHex(hsl.h, hsl.s, 10)}, 
                  ${hslToHex(hsl.h, hsl.s, 50)}, 
                  ${hslToHex(hsl.h, hsl.s, 90)}
                )`,
              }}
            />
          </div>

          {/* Hex Input & Native Eyedropper */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground font-medium">
              Código Hex
            </Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  value={hexInput}
                  onChange={handleHexInputChange}
                  placeholder="#3B82F6"
                  maxLength={7}
                  className="font-mono text-xs uppercase h-8 pl-3 pr-2"
                />
              </div>

              {/* Native color picker button (eyedropper / system picker shortcut) */}
              <label
                className="relative flex items-center justify-center w-8 h-8 rounded-md border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                title="Conta-gotas / Seletor do Sistema"
              >
                <Pipette className="w-4 h-4 text-muted-foreground" />
                <input
                  type="color"
                  value={isValidHex(color) ? color : "#3b82f6"}
                  onChange={(e) => {
                    const chosen = e.target.value;
                    setHsl(hexToHsl(chosen));
                    setHexInput(chosen.toUpperCase());
                    onChange(chosen);
                  }}
                  className="sr-only"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Preset Swatches Palette */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Paleta Rápida</Label>
        <div className="flex flex-wrap gap-1.5 p-2 bg-muted/20 rounded-lg border border-border/50 max-h-24 overflow-y-auto">
          {modernTagPresets.map((swatchColor) => {
            const isSelected =
              color.toLowerCase() === swatchColor.toLowerCase();
            return (
              <button
                key={swatchColor}
                type="button"
                onClick={() => {
                  setHsl(hexToHsl(swatchColor));
                  setHexInput(swatchColor.toUpperCase());
                  onChange(swatchColor);
                }}
                className={`w-6 h-6 rounded-full border transition-all transform hover:scale-110 active:scale-95 ${
                  isSelected
                    ? "ring-2 ring-primary ring-offset-2 scale-110 border-transparent shadow-sm"
                    : "border-black/10 hover:border-black/30"
                }`}
                style={{ backgroundColor: swatchColor }}
                title={swatchColor}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
